// src/components/YMap.jsx
import React from 'react';
import PropTypes from 'prop-types';
import { loadYmaps } from '../utils/ymapsLoader';

const defaultControls = ['zoomControl', 'fullscreenControl'];

// public/ASTORIUS_FAVICON_1.svg => доступно по /ASTORIUS_FAVICON_1.svg
const LOGO_URL = '/ASTORIUS_FAVICON_1.svg';

// Подбери под свой дизайн (важно: эти же размеры используются в iconShape/iconOffset)
const ICON_W = 170; // ширина кликабельной области
const ICON_H = 64;  // высота (включая "хвостик")

const markerStyles = `
  .custom-marker{
    position: relative;
    display: inline-block;
    cursor: pointer;
    user-select: none;
    pointer-events: auto;
  }
  .custom-marker__body{
    background:#1a1a1a;
    border-radius:8px;
    padding:8px 12px;
    display:flex;
    align-items:center;
    gap:8px;
    box-shadow:0 2px 8px rgba(0,0,0,.3);
    white-space:nowrap;
    position:relative;
    transition:transform .15s ease, background .15s ease;
    pointer-events: auto;
  }
  .custom-marker__body::after{
    content:'';
    position:absolute;
    bottom:-8px;
    left:50%;
    transform:translateX(-50%);
    border-left:8px solid transparent;
    border-right:8px solid transparent;
    border-top:8px solid #1a1a1a;
  }
  .custom-marker__logo{
    width:20px;
    height:20px;
    display:block;
    flex:0 0 auto;
  }
  .custom-marker__price{
    color:#fff;
    font-size:13px;
    font-weight:600;
    line-height:1.2;
  }
  .custom-marker__title-tooltip{
    position:absolute;
    bottom:calc(100% + 6px);
    left:50%;
    transform:translateX(-50%);
    background:#1a1a1a;
    color:#fff;
    padding:6px 10px;
    border-radius:6px;
    font-size:12px;
    font-weight:500;
    white-space:nowrap;
    opacity:0;
    visibility:hidden;
    transition:opacity .15s ease, visibility .15s ease;
    max-width:220px;
    overflow:hidden;
    text-overflow:ellipsis;
    box-shadow:0 2px 8px rgba(0,0,0,.3);
    pointer-events:none;
    z-index:1000;
  }
  .custom-marker.is-hover .custom-marker__body{
    transform:scale(1.05);
  }
  .custom-marker.is-hover .custom-marker__title-tooltip{
  opacity:1;
  visibility:visible;
  }
`;

function ensureMarkerStyles() {
  const id = 'ymap-custom-markers-style';
  if (document.getElementById(id)) return;
  const styleEl = document.createElement('style');
  styleEl.id = id;
  styleEl.textContent = markerStyles;
  document.head.appendChild(styleEl);
}

function formatPrice(price) {
  if (!price && price !== 0) return 'Цена по запросу';

  const num = Number(price);
  if (Number.isNaN(num)) return String(price);

  if (num >= 1_000_000) {
    const millions = (num / 1_000_000).toFixed(1).replace('.0', '');
    return `от ${millions} млн ₽`;
  }
  if (num >= 1_000) {
    const thousands = Math.round(num / 1_000);
    return `от ${thousands} тыс ₽`;
  }
  return `от ${num.toLocaleString('ru-RU')} ₽`;
}

// Функция проверки валидности координат
const isValidCoordinate = (lat, lng) => {
  const latNum = Number(lat);
  const lngNum = Number(lng);
  return !isNaN(latNum) && !isNaN(lngNum) && 
         latNum >= -90 && latNum <= 90 && 
         lngNum >= -180 && lngNum <= 180 &&
         latNum !== 0 && lngNum !== 0;
};

function YMap({
  apiKey,
  lang = 'ru_RU',
  center = [55.751244, 37.618423],
  zoom = 14,
  markers = [],
  clusterize = false,
  style = { width: '100%', height: '360px' },
  className = '',
  controls = defaultControls,
  onMapReady,
  isLink = false,
}) {
  const ref = React.useRef(null);
  const mapRef = React.useRef(null);
  const ymapsRef = React.useRef(null);
  const clusterRef = React.useRef(null);
  const layoutRef = React.useRef(null);
  const [ready, setReady] = React.useState(false);
  const [error, setError] = React.useState(null);

  // 1) стили
  React.useEffect(() => {
    ensureMarkerStyles();
  }, []);

  // 2) init карты один раз по apiKey/lang
  React.useEffect(() => {
    let destroyed = false;

    (async () => {
      try {
        // Проверяем валидность центра карты
        if (!isValidCoordinate(center[0], center[1])) {
          console.warn('Invalid map center coordinates:', center);
          setError('Некорректные координаты центра карты');
          return;
        }

        const ymaps = await loadYmaps(apiKey, lang);
        if (destroyed || !ref.current) return;

        ymapsRef.current = ymaps;

        // Один layout на все маркеры (данные берутся из properties.*)
        layoutRef.current = ymaps.templateLayoutFactory.createClass(`
          <div class="custom-marker" data-mid="{{ properties.mid }}">
            <div class="custom-marker__title-tooltip">{{ properties.title }}</div>
            <div class="custom-marker__body">
              <img class="custom-marker__logo" src="${LOGO_URL}" alt="" />
              <span class="custom-marker__price">{{ properties.priceText }}</span>
            </div>
          </div>
        `);

        mapRef.current = new ymaps.Map(
          ref.current,
          { center, zoom, controls },
          { suppressMapOpenBlock: true }
        );
        setReady(true);
        setError(null);

        if (onMapReady) onMapReady(mapRef.current, ymaps);

        if (clusterize) {
          clusterRef.current = new ymaps.Clusterer({
            preset: 'islands#invertedDarkBlueClusterIcons',
            groupByCoordinates: false,
          });
          mapRef.current.geoObjects.add(clusterRef.current);
        }

        // fit
        setTimeout(() => {
          try { mapRef.current?.container.fitToViewport(); } catch {}
        }, 50);
      } catch (err) {
        console.error('Ошибка инициализации карты:', err);
        setError(err.message || 'Ошибка загрузки карты');
      }
    })();

    return () => {
      destroyed = true;
      try {
        mapRef.current?.destroy();
      } catch {}
      mapRef.current = null;
      ymapsRef.current = null;
      clusterRef.current = null;
      layoutRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, lang]);

  // 3) обновление центра/зума
  React.useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (Array.isArray(center) && isValidCoordinate(center[0], center[1])) {
      try { map.setCenter(center, zoom, { duration: 150 }); } catch {}
    }
  }, [center?.[0], center?.[1], zoom]);

  // 4) обновление маркеров/кластеризации
  React.useEffect(() => {
    if (ready) renderMarkers(markers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(markers), clusterize, isLink, ready]);

  function renderMarkers(list) {
    const ymaps = ymapsRef.current;
    const map = mapRef.current;
    const layout = layoutRef.current;
    if (!ymaps || !map || !layout) return;

    const valid = (m) => {
      const coords = Array.isArray(m.coords) ? m.coords : [m.lat, m.lng];
      return Array.isArray(coords) && isValidCoordinate(coords[0], coords[1]);
    };

    const makePlacemark = (m) => {
      const coords = Array.isArray(m.coords) ? m.coords : [m.lat, m.lng];
      const hasLink = isLink && (m.slug || m.id);
      const link = hasLink ? `/realty/${m.slug || m.id}` : null;
      const mid = String(m.id ?? m.slug ?? `${coords[0]}_${coords[1]}`);
      const pm = new ymaps.Placemark(
        coords,
        {
          title: m.title || '',
          priceText: formatPrice(m.price),
          mid
        },
        {
          iconLayout: layout,

          // Критично: shape задаём в координатах ОТНОСИТЕЛЬНО iconOffset
          // iconOffset сдвигает левый-верх иконки относительно точки.
          iconOffset: [-ICON_W / 2, -ICON_H],
          iconShape: {
            type: 'Rectangle',
            coordinates: [
              [0, 0],
              [ICON_W, ICON_H],
            ],
          },

          hasBalloon: false,
          hasHint: false,
        }
      );

      pm.events.add('mouseenter', () => {
        setTimeout(() => {
          const el = document.querySelector(`.custom-marker[data-mid="${CSS.escape(mid)}"]`);
          el?.classList.add('is-hover');
        }, 0);
      });
      
      pm.events.add('mouseleave', () => {
        const el = document.querySelector(`.custom-marker[data-mid="${CSS.escape(mid)}"]`);
        el?.classList.remove('is-hover');
      });

      if (hasLink && link) {
        pm.events.add('click', (e) => {
          // на всякий случай, чтобы карта не схватила жест
          try { e?.stopPropagation?.(); } catch {}
          window.location.href = link;
        });
      }

      return pm;
    };

    const geoObjects = (Array.isArray(list) ? list : [])
      .filter(valid)
      .map(makePlacemark);

    if (clusterize) {
      if (!clusterRef.current) {
        clusterRef.current = new ymaps.Clusterer({
          preset: 'islands#invertedDarkBlueClusterIcons',
          groupByCoordinates: false,
        });
        mapRef.current.geoObjects.add(clusterRef.current);
      }
      clusterRef.current.removeAll();
      if (geoObjects.length) {
        clusterRef.current.add(geoObjects);
      }
    } else {
      // если раньше был кластер — оставим его, но очистим, а на карту повесим обычные
      if (clusterRef.current) clusterRef.current.removeAll();
      mapRef.current.geoObjects.removeAll();
      geoObjects.forEach((pm) => mapRef.current.geoObjects.add(pm));
    }

    // bounds (опционально)
    try {
      if (geoObjects.length === 1) {
        mapRef.current.setCenter(geoObjects[0].geometry.getCoordinates(), zoom, { duration: 200 });
      } else if (geoObjects.length > 1) {
        const b = ymaps.geoQuery(geoObjects).getBounds();
        if (b) mapRef.current.setBounds(b, { checkZoomRange: true, zoomMargin: 40 });
      }
    } catch {}
  }


  return (
    <div ref={ref} className={className} style={style}>
      {error && (
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(220, 53, 69, 0.9)',
          color: 'white',
          padding: '10px 15px',
          borderRadius: '5px',
          textAlign: 'center',
          zIndex: 1000,
          fontSize: '14px'
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

YMap.propTypes = {
  apiKey: PropTypes.string,
  lang: PropTypes.string,
  center: PropTypes.array,
  zoom: PropTypes.number,
  markers: PropTypes.arrayOf(PropTypes.shape({
    coords: PropTypes.array,
    lat: PropTypes.number,
    lng: PropTypes.number,
    title: PropTypes.string,
    price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    slug: PropTypes.string,
  })),
  clusterize: PropTypes.bool,
  style: PropTypes.object,
  className: PropTypes.string,
  controls: PropTypes.array,
  onMapReady: PropTypes.func,
  isLink: PropTypes.bool,
};

export default React.memo(YMap);